/**
 * The in-play game engine wiring shared by the account table (/in-play) and
 * the guest table (/table): websocket subscription, redux sync, bot turns,
 * lobby pings, round clearing, card selection and win detection.
 *
 * Presentation (top bars, coins, celebrations, results) stays in the pages.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { dispatch, Store } from '@/redux';
import { setInPlayState } from '@/redux/reducers';
import { LoggerService } from '@/shared/logger';
import { WEBSOCKET_URLS } from '@/shared/consts';
import { useLandscapeMode } from '@/shared/hooks';
import { Card, InPlayRoomWsResponse } from '../types';
import { CardPosition } from './components/cards-with-layout';
import { useThrowCard } from './context';
import { useBotPlay } from './hooks';
import { useSendSocketMessage } from '../send-message-ctx';
import { useWebsocket } from '../use-websocket';
import { SelectedCard, usePlayCard } from './use-play-card';

export type { SelectedCard } from './use-play-card';

export const useInPlayEngine = () => {
  const { id: userID } = useSelector((state: Store) => state.userReducer);
  const {
    socketError,
    sendMessage,
    socketResponse: inPlayResponse,
    connectionState,
    reconnect,
  } = useWebsocket<InPlayRoomWsResponse>(WEBSOCKET_URLS().ROOM);

  useEffect(() => {
    if (inPlayResponse) {
      dispatch(setInPlayState(inPlayResponse));
    }
  }, [inPlayResponse]);

  const { gameInfo } = inPlayResponse || { gameInfo: null };
  const { gameState, setGameState } = useThrowCard();
  useBotPlay(inPlayResponse!, sendMessage);

  // While seats are filling, poll so new arrivals appear for everyone.
  useEffect(() => {
    if (gameInfo?.room?.isFull) {
      return;
    }
    const t = setInterval(() => {
      sendMessage('ping');
    }, 1500);
    return () => clearInterval(t);
  }, [gameInfo, sendMessage]);

  const { setSendMessage, sendMessage: msgSend } = useSendSocketMessage();
  useEffect(() => {
    if (sendMessage && !msgSend) {
      LoggerService.info('Setting sendMessage in InPlayPage');
      setSendMessage(() => sendMessage);
    }
  }, [sendMessage, setSendMessage, msgSend]);

  useEffect(() => {
    if (gameState === 'clear') {
      sendMessage('clear');
      setGameState('playing');
    }
  }, [gameState, setGameState, sendMessage]);

  const [selectedCard, setSelectedCard] = useState<SelectedCard>(null);
  const ledSuit = gameInfo?.inPlayCards?.[0]?.suite;
  // The raised (in-flight) card belongs to the turn in which it was played;
  // drop the overlay when the turn or led suit moves on.
  useEffect(() => {
    setSelectedCard(null);
  }, [gameInfo?.room?.nextTurn, ledSuit]);

  const userDeck = useMemo(
    () => inPlayResponse?.gameInfo?.userDeck || [],
    [inPlayResponse]
  );

  // A single click / tap / Enter on a legal card submits it immediately.
  const { playCard } = usePlayCard({
    gameInfo,
    userID,
    userDeck,
    sendMessage,
    setSelectedCard,
  });
  const onClickCard = useCallback(
    (card: Card, pos: CardPosition) => () => {
      playCard(card, pos);
    },
    [playCard]
  );

  const lockDrag = useMediaQuery<Theme>(theme => theme.breakpoints.up('lg'));
  const isLandscapeMode = useLandscapeMode();

  // The local player escaped (no cards left while the round moved on).
  const [showWinCelebration, setShowWinCelebration] = useState(false);
  const showWinMessage = useRef(false);
  useEffect(() => {
    if (
      inPlayResponse?.gameInfo?.inPlayCards?.length === 0 &&
      !inPlayResponse?.gameInfo?.userDeck?.length &&
      inPlayResponse?.gameInfo?.room?.nextTurn !== userID &&
      !showWinMessage.current &&
      !inPlayResponse?.gameInfo?.room?.coinsRewarded
    ) {
      LoggerService.info('User has no cards left, user won the game');
      showWinMessage.current = true;
      setShowWinCelebration(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPlayResponse]);

  return {
    userID,
    inPlayResponse,
    gameInfo,
    socketError,
    connectionState,
    reconnect,
    sendMessage,
    selectedCard,
    setSelectedCard,
    onClickCard,
    userDeck,
    lockDrag,
    isLandscapeMode,
    showWinCelebration,
    setShowWinCelebration,
  };
};
